import { Head, Link, router, useForm } from "@inertiajs/react"
import { type FormEvent, useEffect, useRef, useState } from "react"

import AppLogo from "@/components/app-logo"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"

interface Props {
  email_address: string
  dev_magic_link_code: string | null
}

export default function MagicLink({
  email_address,
  dev_magic_link_code,
}: Props) {
  const { data, setData, post, processing, errors } = useForm({
    code: "",
  })
  const [shakeNonce, setShakeNonce] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)

  // Auto-focus input on mount
  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    post("/session/magic_link", {
      onError: () => {
        setShakeNonce((n) => n + 1)
      },
    })
  }

  // Format code as user types
  const handleCodeChange = (value: string) => {
    const cleaned = value
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, "")
      .slice(0, 6)
    setData("code", cleaned)
  }

  // Auto-submit on paste only (not on typing)
  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData.getData("text")
    const cleaned = pasted
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, "")
      .slice(0, 6)

    if (cleaned.length === 6) {
      e.preventDefault()
      setData("code", cleaned)
      router.post(
        "/session/magic_link",
        { code: cleaned },
        { onError: () => setShakeNonce((n) => n + 1) },
      )
    }
  }

  return (
    <>
      <Head title="Enter verification code" />

      <div className="vignette noise bg-bg-body flex min-h-screen items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="mb-8 text-center">
            <Link href="/" className="inline-flex items-center justify-center">
              <AppLogo />
            </Link>
          </div>

          <div
            key={shakeNonce}
            className={`border-border bg-bg-card rounded-lg border p-8 shadow-lg transition-transform ${
              errors.code ? "animate-shake" : ""
            }`}
          >
            <h1 className="font-display mb-2 text-center text-2xl tracking-wide">
              Check your email
            </h1>

            <p className="text-bone-muted mb-6 text-center text-sm">
              Enter the verification code sent to{" "}
              <strong className="text-bone">{email_address}</strong>
            </p>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="flex justify-center">
                <Input
                  ref={inputRef}
                  type="text"
                  value={data.code}
                  onChange={(e) => handleCodeChange(e.target.value)}
                  onPaste={handlePaste}
                  maxLength={6}
                  autoComplete="one-time-code"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck={false}
                  placeholder="------"
                  className="h-16 w-48 text-center font-mono text-3xl tracking-[0.5em]"
                  data-1p-ignore
                  data-lpignore="true"
                />
              </div>

              {errors.code && (
                <p className="text-red text-center text-sm">{errors.code}</p>
              )}

              <Button
                type="submit"
                className="w-full"
                disabled={processing || data.code.length < 6}
              >
                Verify
              </Button>
            </form>

            <p className="text-bone-dim mt-6 text-center text-xs">
              Code expires in 15 minutes.
            </p>

            <div className="mt-4 text-center">
              <Link
                href="/session/new"
                className="text-bone-muted hover:text-bone text-sm transition-colors"
              >
                Use a different email
              </Link>
            </div>
          </div>

          {/* Development mode: Show magic link code */}
          {dev_magic_link_code && (
            <div className="mt-6 rounded-lg border-2 border-dashed border-amber-500/50 bg-amber-500/10 p-4 text-center">
              <span className="mr-2 rounded bg-amber-500 px-2 py-0.5 text-xs font-bold text-white">
                DEV
              </span>
              <span className="text-bone-muted text-sm">Your code:</span>
              <code className="text-bone ml-2 font-mono text-xl font-bold tracking-widest">
                {dev_magic_link_code}
              </code>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-4px); }
          20%, 40%, 60%, 80% { transform: translateX(4px); }
        }
        .animate-shake {
          animation: shake 0.5s ease-in-out;
        }
      `}</style>
    </>
  )
}
